import { useEffect, useState, Fragment } from "react";
import { supabase, getAuthToken } from "../lib/supabase";
import toast from "react-hot-toast";
import dayjs from "dayjs";

type Client = { id: string; raison_sociale: string; email: string };
type Invoice = {
  id: string;
  date: string;
  amount: number;
  status: string;
  url: string;
  reference?: string;
};

export default function BillingFollowUp() {
  const [clients, setClients] = useState<Client[]>([]);
  const [invoices, setInvoices] = useState<Record<string, Invoice[]>>({});
  const [loading, setLoading] = useState<string | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [refInput, setRefInput] = useState<string>("");

  // New filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "unpaid">("all");
  const [monthFilter, setMonthFilter] = useState<string>("");

  useEffect(() => {
    loadClients();
  }, []);

  async function loadClients() {
    const { data: rawClients } = await supabase.from("clients").select("id, raison_sociale");
    const { data: profiles } = await supabase.from("profiles").select("id, email");

    const enriched =
      rawClients?.map((c) => ({
        ...c,
        email: profiles?.find((p) => p.id === c.id)?.email || "N/A",
      })) || [];

    setClients(enriched);
    for (const c of enriched) await loadInvoices(c.id);
  }

  async function loadInvoices(clientId: string) {
    const { data } = await supabase
      .from("invoices")
      .select("id, date, amount, status, url, reference")
      .eq("client_id", clientId)
      .order("date", { ascending: false });

    if (data) setInvoices((prev) => ({ ...prev, [clientId]: data }));
  }

  async function updateInvoiceStatus(id: string, status: string, reference?: string) {
    const update: any = { status };
    if (reference !== undefined) update.reference = reference;
    const { error } = await supabase.from("invoices").update(update).eq("id", id);
    if (error) toast.error("Erreur maj statut");
  }

  async function generateInvoice(clientId: string) {
    setLoading(clientId);
    const token = await getAuthToken();
    const res = await fetch(
      `https://whatsapp-healthcare.onrender.com/billing/generate/${clientId}`,
      { method: "POST", headers: { Authorization: token ?? "" } }
    );
    const json = await res.json();
    if (res.ok) {
      toast.success("Invoice generated!");
      await loadInvoices(clientId);
    } else toast.error(json.detail || "Erreur gen invoice");
    setLoading(null);
  }

  async function deleteInvoice(invoiceId: string, clientId: string) {
    if (!window.confirm("Supprimer cette facture ? Cette action est irréversible.")) return;
    const token = await getAuthToken();
    const res = await fetch(
      `https://whatsapp-healthcare.onrender.com/billing/invoice/${invoiceId}`,
      { method: "DELETE", headers: { Authorization: token ?? "" } }
    );
    const json = await res.json();
    if (!res.ok) toast.error(json.error || "Erreur suppression");
    else {
      toast.success("Facture supprimée");
      await loadInvoices(clientId);
    }
  }

  async function sendInvoice(invoiceId: string) {
    const token = await getAuthToken();
    const res = await fetch(
      `https://whatsapp-healthcare.onrender.com/billing/send/${invoiceId}`,
      { method: "POST", headers: { Authorization: token ?? "" } }
    );
    const json = await res.json();
    res.ok
      ? toast.success(json.detail + (json.provider ? ` (${json.provider})` : ""))
      : toast.error(json.error || json.detail || "Erreur envoi");
  }

  function groupByMonth(invList: Invoice[]) {
    return invList.reduce((acc, inv) => {
      const key = dayjs(inv.date).format("MMMM YYYY");
      if (!acc[key]) acc[key] = [];
      acc[key].push(inv);
      return acc;
    }, {} as Record<string, Invoice[]>);
  }

  // 🧮 Calcul KPI
  const now = dayjs();
  const months = [0, 1, 2].map((offset) => now.subtract(offset, "month").format("MMMM YYYY"));
  
  const kpi = months.map((label) => {
    let all = 0;
    let unpaid = 0;
  
    for (const clientId in invoices) {
      for (const inv of invoices[clientId]) {
        const month = dayjs(inv.date).format("MMMM YYYY");
        if (month === label) {
          all++;
          if (inv.status === "unpaid") unpaid++;
        }
      }
    }
  
    const percent = all > 0 ? Math.round((unpaid / all) * 100) : 0;
    return { label, percent };
  });
  
  const totalUnpaid = Object.values(invoices)
    .flat()
    .filter((i) => i.status === "unpaid")
    .reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <h1 className="text-2xl font-bold mb-6">Billing Follow-Up (Admin)</h1>

      {/* 🔢 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 mt-4">
        {kpi.map(({ label, percent }) => (
          <div key={label} className="bg-gray-50 rounded-xl p-4 shadow border text-center">
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div
              className={`text-2xl font-bold ${
                percent <= 10
                  ? "text-green-600"
                  : percent <= 30
                  ? "text-orange-500"
                  : "text-red-600"
              }`}
            >
              {percent}% unpaid
            </div>
          </div>
        ))}
      
        <div className="bg-gray-50 rounded-xl p-4 shadow border text-center">
          <div className="text-xs text-gray-500 mb-1">Total unpaid</div>
          <div className="text-2xl font-bold text-blue-600">
            {totalUnpaid.toFixed(2)} SGD
          </div>
        </div>
      </div>

      {/* 🔍 Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by name or email"
          className="border rounded px-3 py-1 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="paid">Paid only</option>
          <option value="unpaid">Unpaid only</option>
        </select>

        <input
          type="text"
          placeholder="Month (e.g. September 2025)"
          className="border rounded px-3 py-1 text-sm"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        />
      </div>

      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left">
            <th className="px-4 py-2">Client</th>
            <th className="px-4 py-2">Email</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {clients
            .filter(
              (c) =>
                c.raison_sociale.toLowerCase().includes(search.toLowerCase()) ||
                c.email.toLowerCase().includes(search.toLowerCase())
            )
            .map((c) => {
              const grouped = groupByMonth(invoices[c.id] || []);
              return (
                <Fragment key={c.id}>
                  <tr className="bg-gray-100 border-t">
                    <td className="px-4 py-2 font-semibold">{c.raison_sociale}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{c.email}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => generateInvoice(c.id)}
                        disabled={loading === c.id}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loading === c.id ? "Generating…" : "Generate"}
                      </button>
                    </td>
                  </tr>

                  {Object.entries(grouped)
                    .filter(([month]) =>
                      month.toLowerCase().includes(monthFilter.toLowerCase())
                    )
                    .map(([month, list]) => {
                      const filteredList =
                        statusFilter === "all"
                          ? list
                          : list.filter((inv) => inv.status === statusFilter);

                      if (filteredList.length === 0) return null;

                      return (
                        <Fragment key={month}>
                          <tr>
                            <td colSpan={3} className="px-4 pt-3 text-xs text-gray-500 italic">
                              {month}
                            </td>
                          </tr>
                          {filteredList.map((inv) => (
                            <tr key={inv.id} className="border-t">
                              <td colSpan={3} className="px-4 py-2">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm text-gray-700 whitespace-nowrap">
                                    {dayjs(inv.date).format("DD/MM/YYYY")} –{" "}
                                    {inv.amount.toFixed(2)} SGD
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-xs px-2 py-1 rounded-full ${
                                        inv.status === "paid"
                                          ? "bg-green-100 text-green-700"
                                          : "bg-yellow-100 text-yellow-700"
                                      }`}
                                    >
                                      {inv.status.toUpperCase()}
                                    </span>

                                    <button
                                      className={`text-xs rounded-full px-3 py-1 transition ${
                                        inv.status === "paid"
                                          ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                      onClick={() => {
                                        if (inv.status === "unpaid") {
                                          setEditingInvoiceId(inv.id);
                                          setRefInput(inv.reference || "");
                                        } else {
                                          updateInvoiceStatus(inv.id, "unpaid");
                                          setEditingInvoiceId(null);
                                        }
                                      }}
                                    >
                                      {inv.status === "paid" ? "Mark Unpaid" : "Mark Paid"}
                                    </button>

                                    {editingInvoiceId === inv.id && (
                                      <>
                                        <input
                                          type="text"
                                          placeholder="Réf bancaire"
                                          className="text-xs border rounded px-2 py-1 w-48"
                                          value={refInput}
                                          onChange={(e) => setRefInput(e.target.value)}
                                        />
                                        <button
                                          onClick={async () => {
                                            await updateInvoiceStatus(
                                              inv.id,
                                              "paid",
                                              refInput
                                            );
                                            setEditingInvoiceId(null);
                                            await loadInvoices(c.id);
                                          }}
                                          className="text-xs text-green-600 hover:underline"
                                        >
                                          ✅
                                        </button>
                                      </>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-4 text-xs mt-1 md:mt-0">
                                    <a
                                      href={inv.url}
                                      target="_blank"
                                      className="text-blue-600 hover:underline"
                                    >
                                      PDF
                                    </a>
                                    <button
                                      onClick={() => sendInvoice(inv.id)}
                                      className="bg-green-600 hover:bg-green-700 text-white rounded px-2 py-1"
                                    >
                                      Send
                                    </button>
                                    <button
                                      onClick={() => deleteInvoice(inv.id, c.id)}
                                      className="bg-red-100 hover:bg-red-200 text-red-700 rounded px-2 py-1"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>

                                {inv.reference && (
                                  <div className="text-xs text-gray-400 italic mt-1">
                                    🏦 Ref: {inv.reference}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
